import { format } from 'date-fns';
import { toDate } from '../../../shared/utils/dateHelpers';
import { Appointment, DashboardClient, ProgrammeFamily } from './types';

/** The fields of a comprehensive_details document the dashboard reads. */
export interface RawClient {
  client_id?: string | number;
  client_name?: string;
  active_programme?: boolean;
  current_programme?: string;
  primary_location?: string;
  lead_coach_id?: string | number;
  lead_coach_name?: string;
  first_session_date?: unknown;
  visit_dates?: unknown[];
  visit_history?: { session_date?: unknown; pricing_option?: string; staff_id?: string }[];
  first_ignition_session_date?: unknown;
  ignition_expiration_date?: unknown;
  first_foundation_session_date?: unknown;
  foundation_expiration_date?: unknown;
  foundation_plus_expiration_date?: unknown;
  first_pre_post_natal_date?: unknown;
  pre_post_natal_expiration_date?: unknown;
  pt_sessions?: number;
  foundation_pt_sessions?: number;
  foundation_plus_pt_sessions?: number;
  pre_post_natal_pt_sessions?: number;
  nutrition_sessions?: number;
  metabolic_screenings?: number;
  physical_performance_screenings?: number;
  foundation_allied_health_sessions?: number;
  foundation_physical_performance_sessions?: number;
  foundation_nutrition_sessions?: number;
  report_status?: string | null;
  upcoming_appointments?: {
    coach?: string;
    coach_id?: number | string;
    location?: string;
    date?: { value?: string } | string;
  }[];
}

/** A client document as loaded: its Firestore id, the business id and the raw fields. */
export interface LoadedClient {
  docId: string;
  clientId: string;
  raw: RawClient;
}

const DAY_MS = 24 * 60 * 60 * 1000;

const titleCase = (value: string) =>
  value
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

/** "TAN, Mei Lin" → "Mei Lin Tan"; anything else is title-cased as it stands. */
export function formatPersonName(name?: string | null): string {
  if (!name) return '';
  const parts = name.split(',').map((part) => part.trim());
  if (parts.length !== 2) return titleCase(name);
  return `${titleCase(parts[1])} ${titleCase(parts[0])}`;
}

/** The mirror's location names, shortened the way staff say them. */
export function locationLabel(location?: string | null): string {
  if (!location) return 'No location';
  const lower = location.toLowerCase();
  if (lower.includes('cbd')) return 'Club Street';
  if (lower.includes('one north')) return 'one-north';
  return location;
}

export const isIgnition2025 = (raw: RawClient): boolean => {
  const programme = raw.current_programme?.toLowerCase() ?? '';
  return programme.includes('ignition') && programme.includes('2025');
};

/** Total PT sessions in the programme name ("Ignition Programme 2025 | 22 PT"); promo packages have 4. */
export function programmePtTotal(programmeName?: string): number | null {
  if (!programmeName) return null;
  if (programmeName.toLowerCase().includes('promo')) return 4;
  const match = programmeName.match(/(\d+)\s*PT/i);
  return match ? parseInt(match[1], 10) : null;
}

/** YYYY-MM-DD of an appointment or visit, read from the stored string so the bogus "Z" never shifts the day. */
export function localDateKey(value: unknown): string {
  if (!value) return '';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.substring(0, 10);
  const date = toDate(value);
  return date ? format(date, 'yyyy-MM-dd') : '';
}

function localTime(value: unknown): { hours: number; minutes: number } | null {
  const text = typeof value === 'string' ? value : toDate(value)?.toISOString();
  const match = text?.match(/T(\d{2}):(\d{2})/);
  if (!match) return null;
  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  return hours <= 23 && minutes <= 59 ? { hours, minutes } : null;
}

function parseAppointments(raw: RawClient): Appointment[] {
  return (raw.upcoming_appointments ?? [])
    .map((appointment) => {
      const value = typeof appointment.date === 'string' ? appointment.date : appointment.date?.value;
      const coachId = appointment.coach_id === undefined || appointment.coach_id === null ? NaN : Number(appointment.coach_id);
      return {
        coachId: Number.isNaN(coachId) ? null : coachId,
        coachName: formatPersonName(appointment.coach),
        location: locationLabel(appointment.location),
        date: localDateKey(value),
        time: localTime(value),
      };
    })
    .filter((appointment) => appointment.date !== '')
    .sort((a, b) => (a.date + timeKey(a)).localeCompare(b.date + timeKey(b)));
}

const timeKey = (appointment: Appointment) =>
  appointment.time ? `${String(appointment.time.hours).padStart(2, '0')}${String(appointment.time.minutes).padStart(2, '0')}` : '9999';

/** First session the check-in and success rules count from: the stored date, else the earliest visit. */
export function firstSessionDate(raw: RawClient): Date | null {
  if (raw.first_session_date) return toDate(raw.first_session_date);
  const visits = (raw.visit_dates ?? [])
    .map((value) => toDate(value))
    .filter((date): date is Date => date !== null)
    .sort((a, b) => a.getTime() - b.getTime());
  return visits[0] ?? null;
}

export interface CurrentProgramme {
  family: ProgrammeFamily;
  label: string;
  start: Date | null;
  /** The programme's own end date, not latest_expiration_date. */
  end: Date | null;
  ptUsed: number | null;
}

/**
 * The programme a client is on now, read from `current_programme`. The
 * is_*_client flags mean "has ever been on it", so they are never used here.
 * Each programme has its own end date field; Foundation Plus (a "+" in the
 * name, as on the Foundation overview) has its own too.
 */
export function currentProgramme(raw: RawClient): CurrentProgramme | null {
  const name = raw.current_programme?.toLowerCase() ?? '';
  if (name.includes('ignition')) {
    return { family: 'ignition', label: 'Ignition', start: toDate(raw.first_ignition_session_date), end: toDate(raw.ignition_expiration_date), ptUsed: raw.pt_sessions ?? null };
  }
  if (name.includes('foundation')) {
    const plus = name.includes('+');
    return {
      family: 'foundation',
      label: plus ? 'Foundation Plus' : 'Foundation',
      start: toDate(raw.first_foundation_session_date),
      end: toDate(plus ? raw.foundation_plus_expiration_date : raw.foundation_expiration_date),
      ptUsed: (plus ? raw.foundation_plus_pt_sessions : raw.foundation_pt_sessions) ?? null,
    };
  }
  if (name.includes('pre/post natal')) {
    return {
      family: 'natal',
      label: 'Pre/Post Natal',
      start: toDate(raw.first_pre_post_natal_date),
      end: toDate(raw.pre_post_natal_expiration_date),
      ptUsed: raw.pre_post_natal_pt_sessions ?? null,
    };
  }
  return null;
}

export function toDashboardClient(loaded: LoadedClient, now: Date): DashboardClient {
  const { raw } = loaded;
  const programme = currentProgramme(raw);
  const start = programme?.start ?? null;

  return {
    docId: loaded.docId,
    clientId: loaded.clientId,
    name: formatPersonName(raw.client_name) || `Client #${loaded.clientId}`,
    coachId: raw.lead_coach_id === undefined || raw.lead_coach_id === null ? null : String(raw.lead_coach_id),
    coachName: formatPersonName(raw.lead_coach_name) || 'No lead coach',
    location: locationLabel(raw.primary_location),
    programme: programme?.family ?? null,
    programmeLabel: programme?.label ?? (raw.current_programme || 'No programme'),
    week: start && start.getTime() <= now.getTime() ? Math.floor((now.getTime() - start.getTime()) / (7 * DAY_MS)) + 1 : null,
    ptUsed: programme?.ptUsed ?? null,
    ptTotal: programmePtTotal(raw.current_programme),
    endsAt: programme?.end ?? null,
    appointments: parseAppointments(raw),
    activeProgramme: raw.active_programme === true,
  };
}
